import React, { useId } from "react";
import { Avatar, Badge } from "../../../ForBrands/ui/BrandUI";
import { ListRule, MetaCols, MobileCard, MobileEmpty } from "../../../ForBrands/ui/mobile/MobileUI";
import { EMPTY, classificationOf, initialsOf, playerMeta, poolTag, rowId } from "../allocationModel";
import { AddedTag, AllocatedPayout, Money, NilAllocated, POOLS, PlayerName, SportScore, VisibilityScore, eyeLabel, photoOf } from "./allocationCells";
import { EyeToggle } from "./allocationUi";

// Figma 03b · CacheShare on phones, option B (2416:2527, card 2416:2628):
// one card per player with both pools, each under its own eye, so nothing
// the desktop tables show sits behind a switch (D-cs1). A player hidden in
// a pool dims that pool's figures only; the card turns subtle while the
// player is out of either pool. The cells are the tables' own
// (allocationCells.js), so both layouts read the same; the scores carry
// "/10" here ("6.4/10"), where no "(out of 10)" header says it. Each pool is
// two rows of two with its Allocated box bottom right: [Sport score | Team] /
// [All players | Allocated] and [Visibility score | NIL payout] /
// [ | Allocated].

const SKELETON_CARDS = 3;
const VALUE = "bm-label-l";

// One pool: the overline with its eye, then the figures (dimmed while the
// player is hidden in this pool; the eye stays full so it can be undone).
const PoolSection = ({ row, pool, onToggle, children }) => {
  const id = useId();
  const out = !!row[POOLS[pool].flag];
  return (
    <div role="group" aria-labelledby={id} className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <p id={id} className="min-w-0 flex-1 bm-overline">
          {POOLS[pool].group}
        </p>
        <EyeToggle hidden={out} iconSize={20} onToggle={() => onToggle(rowId(row), pool)} label={eyeLabel(row, pool, out)} />
      </div>
      <div className={`flex flex-col gap-3 ${out ? "opacity-40" : ""}`}>{children}</div>
    </div>
  );
};

const PlayerCard = ({ row, onToggle, onLock, onOpenProfile, onRemove }) => {
  const tag = poolTag(row);
  const meta = playerMeta(row);
  const outRs = !!row.hiddenRs;
  return (
    <MobileCard as="li" tone={outRs || row.hiddenNil ? "subtle" : "white"}>
      <div className="flex items-center gap-3">
        <Avatar src={photoOf(row.avatar)} initials={initialsOf(row.playerName)} size={32} tone="outline" />
        <div className="flex min-w-0 flex-1 flex-col gap-[3px] break-words">
          <PlayerName row={row} onOpenProfile={onOpenProfile} wrap />
          {(meta || tag || row.added) && (
            <div className="flex flex-wrap items-center gap-[6px]">
              {meta && <span className="bm-body-s text-bm-muted">{meta}</span>}
              {row.added && <AddedTag name={row.playerName} onRemove={() => onRemove(rowId(row))} />}
              {tag && (
                <Badge tone="outline" dot="#edd200">
                  {tag}
                </Badge>
              )}
            </div>
          )}
        </div>
        <Badge tone="outline">{classificationOf(row) || EMPTY}</Badge>
      </div>
      <ListRule />
      <PoolSection row={row} pool="rs" onToggle={onToggle}>
        <MetaCols
          items={[
            { key: "score", label: "Sport score", value: <SportScore row={row} className={VALUE} suffix /> },
            { key: "team", label: "Team", value: <Money value={row.payoutTeam} /> },
          ]}
        />
        <MetaCols
          items={[
            { key: "all", label: "All players", value: <Money value={row.payoutAllPlayers} /> },
            { key: "allocated", label: "Allocated", value: <AllocatedPayout card row={row} disabled={outRs} onLock={onLock} /> },
          ]}
        />
      </PoolSection>
      <ListRule />
      <PoolSection row={row} pool="nil" onToggle={onToggle}>
        <MetaCols
          items={[
            { key: "visibility", label: "Visibility score", value: <VisibilityScore row={row} className={VALUE} suffix /> },
            { key: "payout", label: "NIL payout", value: <Money value={row.nilPayout} /> },
          ]}
        />
        <MetaCols
          items={[
            { key: "spacer", label: null, value: null },
            { key: "allocated", label: "Allocated", value: <NilAllocated card row={row} disabled={!!row.hiddenNil} onLock={onLock} /> },
          ]}
        />
      </PoolSection>
    </MobileCard>
  );
};

const Bar = ({ className }) => <span className={`block h-3 animate-pulse rounded-full bg-bm-mutedbg ${className}`} />;

// Card shells with pulse bars while the first roster loads.
const SkeletonCard = () => (
  <MobileCard as="li" aria-hidden="true">
    <div className="flex items-center gap-3">
      <span className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-bm-mutedbg" />
      <Bar className="w-1/2" />
    </div>
    <ListRule />
    <Bar className="w-full max-w-[200px]" />
    <Bar className="w-2/3 max-w-[160px]" />
  </MobileCard>
);

// Same props as AllocationTables (`stacked` has no meaning here).
const AllocationCards = ({ rows, loading, emptyText, onToggle, onLock, onOpenProfile, onRemove }) => {
  if (!loading && !rows.length) return <MobileEmpty>{emptyText}</MobileEmpty>;
  return (
    <ul aria-label="Players" aria-busy={!!loading} className={`flex flex-col gap-3 ${loading && rows.length ? "opacity-60" : ""}`}>
      {loading && !rows.length
        ? Array.from({ length: SKELETON_CARDS }, (_, i) => <SkeletonCard key={i} />)
        : rows.map((r) => (
            <PlayerCard key={rowId(r)} row={r} onToggle={onToggle} onLock={onLock} onOpenProfile={onOpenProfile} onRemove={onRemove} />
          ))}
    </ul>
  );
};

export default AllocationCards;
